/**
 * @fileoverview The terminal (register) lifecycle (`terminals.status`) — its vocabulary, its status sets, its status
 * machine and its badge tones, declared once (REGISTER-STORE-INVARIANT-001 U2a).
 *
 * ## Why it lives here
 *
 * A register's status was spelled four times:
 * - pos-api's `Terminal` model (`VALID_STATUSES`, `SEAT_HOLDING_STATUSES`);
 * - pos-api's `constants/statuses` `TERMINAL_STATUS`;
 * - `@puffin-core/middleware`'s retired copy, which named states the database never held;
 * - pos-web's mirrors (the badge tones, the status words).
 *
 * The register screens offer status MOVES, and a screen can only offer the moves the server accepts if it reads the
 * same machine. So it lives here, beside the print-job and shipment lifecycles, and both apps import it. The
 * cross-app `terminalStatusMatchesCheck` contract holds `TERMINAL_STATUS_VALUES` EQUAL to the `chk_status` CHECK as
 * the migration chain leaves it.
 *
 * ## The states
 *
 * - `registered` — a register at its store, not selling: created here, and back here when deactivated. pos-web calls
 *   it "Parked".
 * - `active` — selling at its store.
 * - `suspended` — held out of service. It can return, so it keeps its seat. Nothing writes it today.
 * - `revoked` — permanently decommissioned. Nothing writes it today.
 * - `unregistered` — retired by a person. A retired register is immutable history (its sales and drawer sessions
 *   RESTRICT its row); a device that comes back gets a NEW register.
 *
 * ## The machine
 *
 * The moves the server makes: activate (registered → active), deactivate (active → registered) and unregister (any
 * status in service → unregistered). A retired register is terminal. Every status is a KEY, a terminal one mapping to
 * `[]`: the `PRINT_JOB_STATUS_TRANSITIONS` convention, so a reader lists a status's targets without knowing which
 * convention was used. pos-api enforces it at the ONE status writer, `Terminal.updateStatus`.
 *
 * @module @puffin-core/status-taxonomy/terminalStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/**
 * The register lifecycle, in lifecycle order.
 *
 * @type {Readonly<Object<string, string>>}
 */
const TERMINAL_STATUS = Object.freeze({
  REGISTERED: 'registered',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REVOKED: 'revoked',
  UNREGISTERED: 'unregistered',
});

/**
 * Every status, in lifecycle order: what the `chk_status` CHECK admits.
 *
 * @type {ReadonlyArray<string>}
 */
const TERMINAL_STATUS_VALUES = Object.freeze(Object.values(TERMINAL_STATUS));

/**
 * The statuses that hold one of the company's register seats (the account's register line). A status that can return
 * keeps its seat; a retired one releases it.
 *
 * @type {ReadonlyArray<string>}
 */
const TERMINAL_SEAT_HOLDING_STATUSES = Object.freeze([
  TERMINAL_STATUS.REGISTERED,
  TERMINAL_STATUS.ACTIVE,
  TERMINAL_STATUS.SUSPENDED,
]);

/**
 * The statuses a register is IN SERVICE at: the same set as seat-holding, by identity, so what the account bills and
 * what the screens show can never drift apart.
 *
 * @type {ReadonlyArray<string>}
 */
const TERMINAL_IN_SERVICE_STATUSES = TERMINAL_SEAT_HOLDING_STATUSES;

/**
 * The statuses of a retired register: the complement of in service.
 *
 * @type {ReadonlyArray<string>}
 */
const TERMINAL_RETIRED_STATUSES = Object.freeze([
  TERMINAL_STATUS.REVOKED,
  TERMINAL_STATUS.UNREGISTERED,
]);

/**
 * The statuses a manager must act on: each holds a paid seat and does not sell (a parked register is a seat paid for
 * nothing). What the dashboard's "Needs attention" counts.
 *
 * @type {ReadonlyArray<string>}
 */
const TERMINAL_ATTENTION_STATUSES = Object.freeze([
  TERMINAL_STATUS.REGISTERED,
  TERMINAL_STATUS.SUSPENDED,
]);

/**
 * Every legal transition, spelled once.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const TERMINAL_STATUS_TRANSITIONS = Object.freeze({
  [TERMINAL_STATUS.REGISTERED]: Object.freeze([
    TERMINAL_STATUS.ACTIVE,
    TERMINAL_STATUS.UNREGISTERED,
  ]),
  [TERMINAL_STATUS.ACTIVE]: Object.freeze([
    TERMINAL_STATUS.REGISTERED,
    TERMINAL_STATUS.UNREGISTERED,
  ]),
  [TERMINAL_STATUS.SUSPENDED]: Object.freeze([TERMINAL_STATUS.UNREGISTERED]),
  [TERMINAL_STATUS.REVOKED]: Object.freeze([]),
  [TERMINAL_STATUS.UNREGISTERED]: Object.freeze([]),
});

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * The statuses a register at `status` may move to next: what the server accepts and a screen offers.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as `constructor`) answers
 * `[]` rather than a function.
 *
 * @param {string} status - The register's current status.
 * @returns {ReadonlyArray<string>} The legal targets, in the machine's order; `[]` when terminal or unknown.
 */
function terminalStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(TERMINAL_STATUS_TRANSITIONS, status)
    ? TERMINAL_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * Each status's badge TONE (`<StatusBadgeCell statusTones>`, Q-MI-25): the tones pos-web's register screens already
 * show. Parked (`info`), selling (`success`), held out (`warning`), decommissioned (`danger`), retired (`neutral`).
 *
 * @type {Readonly<Object<string, string>>}
 */
const TERMINAL_STATUS_BADGE_TONES = Object.freeze({
  [TERMINAL_STATUS.REGISTERED]: BADGE_TONE.INFO,
  [TERMINAL_STATUS.ACTIVE]: BADGE_TONE.SUCCESS,
  [TERMINAL_STATUS.SUSPENDED]: BADGE_TONE.WARNING,
  [TERMINAL_STATUS.REVOKED]: BADGE_TONE.DANGER,
  [TERMINAL_STATUS.UNREGISTERED]: BADGE_TONE.NEUTRAL,
});

module.exports = {
  TERMINAL_STATUS,
  TERMINAL_STATUS_VALUES,
  TERMINAL_SEAT_HOLDING_STATUSES,
  TERMINAL_IN_SERVICE_STATUSES,
  TERMINAL_RETIRED_STATUSES,
  TERMINAL_ATTENTION_STATUSES,
  TERMINAL_STATUS_TRANSITIONS,
  TERMINAL_STATUS_BADGE_TONES,
  terminalStatusTargets,
};
